import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Trophy, FileText, Zap, Star } from "lucide-react";

const GRADES = ['A', 'B', 'C', 'D'];
const QUARTERS = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];

const GRADE_INFO = {
  'A': { label: 'Excellent', color: 'from-emerald-400 to-emerald-600', bg: 'bg-emerald-500', ring: 'ring-emerald-300' },
  'B': { label: 'Good', color: 'from-blue-400 to-blue-600', bg: 'bg-blue-500', ring: 'ring-blue-300' },
  'C': { label: 'Satisfactory', color: 'from-amber-400 to-amber-600', bg: 'bg-amber-500', ring: 'ring-amber-300' },
  'D': { label: 'Developing', color: 'from-orange-400 to-orange-600', bg: 'bg-orange-500', ring: 'ring-orange-300' }
};

const GradeSelector = ({ label, value, onChange, testId }) => (
  <div className="space-y-3">
    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
      <Star className="w-4 h-4 text-amber-500" />
      {label}
    </Label>
    <div className="flex gap-3">
      {GRADES.map((grade) => {
        const info = GRADE_INFO[grade];
        const isSelected = value === grade;
        return (
          <button
            key={grade}
            type="button"
            data-testid={`${testId}-${grade}`}
            onClick={() => onChange(grade)}
            className={`relative w-14 h-14 rounded-xl font-bold text-lg transition-all duration-300 ${isSelected
              ? `bg-gradient-to-br ${info.color} text-white shadow-lg scale-110 ring-4 ${info.ring}`
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:scale-105'
              }`}
          >
            {grade}
            {isSelected && (
              <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-medium text-slate-600">
                {info.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

export default function AddPerformance() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentQuarter = QUARTERS[Math.floor(new Date().getMonth() / 3)];

  const [quarterlyData, setQuarterlyData] = useState({
    quarter: currentQuarter,
    year: currentYear,
    grades: {
      regularity: 'C',
      discipline: 'C',
      performance: {
        passing: 'C',
        shooting: 'C',
        dribbling: 'C',
        heading: 'C',
        ball_control: 'C',
        match_play: 'C'
      }
    },
    remarks: ''
  });

  useEffect(() => {
    fetchStudent();
  }, [studentId]);

  const fetchStudent = async () => {
    try {
      const response = await api.get(`/students/${studentId}`);
      setStudent(response.data);
    } catch (error) {
      toast.error("Failed to load student");
    }
  };

  const handleQuarterlySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/reports/quarterly', {
        student_id: studentId,
        quarter: quarterlyData.quarter,
        year: quarterlyData.year,
        grades: quarterlyData.grades,
        remarks: quarterlyData.remarks || null
      });
      toast.success('Quarterly report submitted successfully!');
      navigate(`/student/${studentId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Premium Header */}
      <nav className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                data-testid="back-to-student-btn"
                className="hover:bg-white/10 text-white -ml-2 rounded-xl"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img src="/gfa-logo.png?v=5" alt="GFA Logo" className="w-10 h-10 object-contain rounded-full bg-white p-0.5" />
              <div>
                <h1 className="text-xl font-bold" data-testid="add-performance-title">Quarterly Assessment</h1>
                <p className="text-sm text-slate-400">{student?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-emerald-500/20 px-4 py-2 rounded-full border border-white/10">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-bold text-white/90">STADIA</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto p-8">
        <Card className="shadow-2xl border-0 rounded-3xl overflow-hidden bg-white/80 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-8 px-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Quarterly Report Card</CardTitle>
                <p className="text-emerald-100 mt-1">Comprehensive performance evaluation</p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-8">
            <form onSubmit={handleQuarterlySubmit} className="space-y-8">
              {/* Period Selection */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Quarter</Label>
                  <Select
                    value={quarterlyData.quarter}
                    onValueChange={(v) => setQuarterlyData({ ...quarterlyData, quarter: v })}
                  >
                    <SelectTrigger data-testid="quarterly-quarter-select" className="h-12 rounded-xl border-2 border-slate-200 focus:border-emerald-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {QUARTERS.map((q) => (
                        <SelectItem key={q} value={q}>{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Year</Label>
                  <Select
                    value={quarterlyData.year.toString()}
                    onValueChange={(v) => setQuarterlyData({ ...quarterlyData, year: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="quarterly-year-select" className="h-12 rounded-xl border-2 border-slate-200 focus:border-emerald-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* General Assessment */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 space-y-8">
                <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                  <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center">
                    <span className="text-sm">📋</span>
                  </div>
                  General Assessment
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <GradeSelector
                    label="Regularity"
                    value={quarterlyData.grades.regularity}
                    onChange={(v) => setQuarterlyData({ ...quarterlyData, grades: { ...quarterlyData.grades, regularity: v } })}
                    testId="quarterly-regularity"
                  />
                  <GradeSelector
                    label="Discipline"
                    value={quarterlyData.grades.discipline}
                    onChange={(v) => setQuarterlyData({ ...quarterlyData, grades: { ...quarterlyData.grades, discipline: v } })}
                    testId="quarterly-discipline"
                  />
                </div>
              </div>

              {/* Football Skills */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 space-y-8">
                <h3 className="font-bold text-emerald-800 text-lg flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-200 rounded-lg flex items-center justify-center">
                    <span className="text-sm">⚽</span>
                  </div>
                  Football Skills Assessment
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <GradeSelector
                    label="Passing"
                    value={quarterlyData.grades.performance.passing}
                    onChange={(v) => setQuarterlyData({
                      ...quarterlyData,
                      grades: { ...quarterlyData.grades, performance: { ...quarterlyData.grades.performance, passing: v } }
                    })}
                    testId="quarterly-passing"
                  />
                  <GradeSelector
                    label="Shooting"
                    value={quarterlyData.grades.performance.shooting}
                    onChange={(v) => setQuarterlyData({
                      ...quarterlyData,
                      grades: { ...quarterlyData.grades, performance: { ...quarterlyData.grades.performance, shooting: v } }
                    })}
                    testId="quarterly-shooting"
                  />
                  <GradeSelector
                    label="Dribbling"
                    value={quarterlyData.grades.performance.dribbling}
                    onChange={(v) => setQuarterlyData({
                      ...quarterlyData,
                      grades: { ...quarterlyData.grades, performance: { ...quarterlyData.grades.performance, dribbling: v } }
                    })}
                    testId="quarterly-dribbling"
                  />
                  <GradeSelector
                    label="Heading"
                    value={quarterlyData.grades.performance.heading}
                    onChange={(v) => setQuarterlyData({
                      ...quarterlyData,
                      grades: { ...quarterlyData.grades, performance: { ...quarterlyData.grades.performance, heading: v } }
                    })}
                    testId="quarterly-heading"
                  />
                  <GradeSelector
                    label="Ball Control"
                    value={quarterlyData.grades.performance.ball_control}
                    onChange={(v) => setQuarterlyData({
                      ...quarterlyData,
                      grades: { ...quarterlyData.grades, performance: { ...quarterlyData.grades.performance, ball_control: v } }
                    })}
                    testId="quarterly-ball-control"
                  />
                  <GradeSelector
                    label="Match Play"
                    value={quarterlyData.grades.performance.match_play}
                    onChange={(v) => setQuarterlyData({
                      ...quarterlyData,
                      grades: { ...quarterlyData.grades, performance: { ...quarterlyData.grades.performance, match_play: v } }
                    })}
                    testId="quarterly-match-play"
                  />
                </div>
              </div>

              {/* Remarks */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-700">Coach's Remarks & Recommendations</Label>
                <Textarea
                  data-testid="quarterly-remarks"
                  placeholder="Share detailed observations about the player's progress, areas of improvement, and recommendations for the next quarter..."
                  value={quarterlyData.remarks}
                  onChange={(e) => setQuarterlyData({ ...quarterlyData, remarks: e.target.value })}
                  rows={5}
                  className="rounded-xl border-2 border-slate-200 focus:border-emerald-500 resize-none"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-14 text-lg font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl shadow-lg shadow-emerald-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/40"
                data-testid="submit-quarterly-btn"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Quarterly Report'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
